import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm shadow-xs font-semibold transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary-fill text-primary-on-fill shadow-sm hover:bg-primary-fill-hovered',
        destructive:
          'bg-error-fill text-error-on-fill hover:bg-error-fill-hovered',
        outline:
          'border border-neutral-muted-border hover:bg-primary-muted-fill hover:text-primary-on-muted-fill',
        secondary:
          'bg-secondary-muted-fill text-secondary-foreground hover:bg-secondary-muted-fill-hovered',
        ghost:
          'hover:bg-primary-muted-fill hover:text-primary-on-muted-fill shadow-none',
        mutedGhost:
          'shadow-none text-neutral-muted-on-fill hover:bg-primary-muted-fill hover:text-primary-on-muted-fill',
        link: 'text-primary underline-offset-4 hover:underline shadow-none',
        neutral:
          'border border-neutral-muted-border bg-neutral-fill hover:bg-neutral-fill-hovered',
        linkPrimary: 'text-primary-on-neutral-fill hover:underline',
        danger:
          'border border-neutral-muted-border bg-neutral-fill text-error-on-neutral-fill hover:bg-neutral-fill-hovered',
        linkMutedPrimary:
          'text-primary-muted-on-neutral-fill hover:underline shadow-none',
        accent:
          'bg-accent-fill text-accent-on-fill hover:bg-accent-fill-hovered',
        mutedAccent:
          'bg-accent-muted-fill text-accent-on-muted-fill hover:bg-accent-muted-fill-hovered',
      },
      size: {
        default: '[&>.lucide]:-translate-y-[0.5px] h-9 px-4 py-2',
        sm: '[&>.lucide]:-translate-y-[0.5px] h-8 rounded-md px-3 text-xs',
        lg: '[&>.lucide]:-translate-y-[0.5px] h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
